import React from 'react';
import { Button, Form, FormGroup, FormControl, Row, Col } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../../../config';
import utils from '../../utils';
import moment from 'moment';
import { handleFocusInScrollField, INVENTORY_REPORT_CARD_SCROLL_STYLE } from './invKeyboardScroll';

class FGTransferSummaryReport extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            id: null,
            reportNo: '',
            refNo: '',
            lotNo: '',
            processDate: '',
            inspection: '',
            partNo: '',
            description: '',
            jobOrderNo: '',
            transactionId: '',
            manufacturingDate: '',
            fromLocationCode: '',
            fromLocationDescription: '',
            toLocationCode: '',
            toLocationDescription: '',
            specialRequirement: '',
            storageLocation: '',
            quantity: 0,
            country: '',
            status: '',
            rejectReason: '',
            remarks: '',
            issuedBy: '',
            acceptedBy: '',
            photoUrl: '',
            loading: true,
        };
    }

    componentDidMount() {
        this.loadTransferData();
    }

    fetchMasterPhotoUrl = async (partNo, company) => {
        if (!partNo || !company) return '';
        try {
            const response = await fetch(
                `${config.apiUrl}/getMasterFinishedGoods?company=${company}&keyword=${encodeURIComponent(partNo)}`,
                { method: 'GET', headers: { 'Content-Type': 'application/json' } }
            );
            if (!response.ok) return '';
            const body = await response.json();
            const list = Array.isArray(body?.data) ? body.data : [];
            const exact = list.find(item => String(item.partNo || '').trim() === String(partNo).trim());
            return exact?.photoUrl || '';
        } catch (error) {
            console.error('Error fetching master FG photo:', error);
            return '';
        }
    }

    loadTransferData = async () => {
        const { id } = this.props.params || {};
        if (!id) return;

        try {
            const response = await fetch(
                `${config.apiUrl}/getOneFGMaterialTransfer?id=${id}`,
                {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            
            if (!response.ok) {
                console.error('Error loading transfer data:', response.status);
                this.setState({ loading: false });
                return;
            }

            const data = await response.json();
            
            if (data) {
                const pickFirstNonEmpty = (...values) => {
                    for (let i = 0; i < values.length; i++) {
                        const value = values[i];
                        if (value !== null && value !== undefined && String(value).trim() !== '') {
                            return value;
                        }
                    }
                    return '';
                };
                const fallbackPhotoUrl = data.photoUrl ? '' : await this.fetchMasterPhotoUrl(data.partNo, data.company);
                const reportNo = data.createdAt && data.id 
                    ? moment(data.createdAt).format('YYYYMMDD') + 'T' + data.id
                    : data.id || '';
                
                // Storage location is now in format: locationCode.FGrow.level.pallet or locationCode.storageCode
                // No need to parse, just use the storageLocation as-is

                this.setState({
                    id: data.id,
                    reportNo: reportNo,
                    refNo: data.transactionId || '',
                    lotNo: data.lotNo || '',
                    processDate: data.createdAt 
                        ? moment(data.createdAt).format('DD/MM/YYYY')
                        : '',
                    inspection: data.status === 'confirmed' ? 'Yes' : data.status === 'rejected' ? 'No' : '',
                    partNo: data.partNo || '',
                    description: data.description || '',
                    jobOrderNo: data.jobOrderNo || '',
                    transactionId: data.transactionId || '',
                    manufacturingDate: data.manufacturingDate 
                        ? moment(data.manufacturingDate).format('DD/MM/YYYY')
                        : '',
                    fromLocationCode: data.fromLocationCode || '',
                    fromLocationDescription: data.fromLocationDescription || '',
                    toLocationCode: data.toLocationCode || '',
                    toLocationDescription: data.toLocationDescription || '',
                    specialRequirement: data.specialRequirement || '',
                    storageLocation: data.storageLocation || '',
                    quantity: data.quantity || 0,
                    country: data.country || '',
                    status: data.status || 'pending_confirmation',
                    rejectReason: data.rejectReason || '',
                    remarks: data.remarks || '',
                    issuedBy: pickFirstNonEmpty(
                        data.created_by_name,
                        data.createdByName,
                        data.created_by,
                        data.createdBy,
                        data.issueBy,
                        data.issuedBy
                    ),
                    acceptedBy: pickFirstNonEmpty(
                        data.updated_by_name,
                        data.updatedByName,
                        data.updated_by,
                        data.updatedBy,
                        data.receivedBy,
                        data.receiveBy,
                        data.acceptedBy
                    ),
                    photoUrl: data.photoUrl || fallbackPhotoUrl || '',
                    loading: false,
                });
            } else {
                this.setState({ loading: false });
            }
        } catch (error) {
            console.error('Error loading transfer data:', error);
            this.setState({ loading: false });
        }
    }

    handleChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value });
    }

    exportExcel = () => {
        const partNo = this.state.partNo || 'unknown';
        const timestamp = this.state.processDate || new Date().toLocaleString();
        utils.tableToExcel('fg-transfer-report-table', 'Report', 'FG_Transfer_report_' + partNo + '_' + timestamp + '.xls');
    }

    render() {
        if (this.state.loading) {
            return <div>Loading...</div>;
        }

        const statusColor = this.state.status === 'confirmed' ? '#28a745' : 
                           this.state.status === 'rejected' ? '#dc3545' : '#ffc107';

        return (
            <div className="container" style={{ marginTop: '20px', backgroundColor: '#f5f5f5', padding: '20px' }}>
                <div className="no-print" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h4>FG Transfer Individual Report</h4>
                    <div>
                        <Button bsStyle="primary" onClick={this.exportExcel}>Export</Button>
                    </div>
                </div>

                <div style={INVENTORY_REPORT_CARD_SCROLL_STYLE} onFocusCapture={handleFocusInScrollField}>
                    <div style={{ marginBottom: '15px', borderBottom: '2px solid #ddd', paddingBottom: '10px' }}>
                        <span style={{ 
                            padding: '5px 15px', 
                            backgroundColor: '#4990E2', 
                            color: '#fff',
                            borderRadius: '3px',
                            fontSize: '14px'
                        }}>FG Transfer Report</span>
                    </div>

                    <Row>
                        <Col md={6} className="report-content">
                            <Form>
                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>Report No:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                type="text"
                                                value={this.state.reportNo}
                                                readOnly
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>Ref No:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                type="text"
                                                name="refNo"
                                                value={this.state.refNo}
                                                readOnly
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>Process Date:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                type="text"
                                                value={this.state.processDate}
                                                readOnly
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>Inspection:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                as="select"
                                                name="inspection"
                                                value={this.state.inspection}
                                                disabled
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            >
                                                <option value="">Select</option>
                                                <option value="Yes">Yes</option>
                                                <option value="No">No</option>
                                            </FormControl>
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <h5 style={{ marginTop: '20px', marginBottom: '15px', borderTop: '1px solid #ddd', paddingTop: '15px' }}>
                                    Part Information
                                </h5>

                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>Part No:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                type="text"
                                                value={this.state.partNo}
                                                readOnly
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>Description:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                type="text"
                                                value={this.state.description}
                                                readOnly
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>Job Order No:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                type="text"
                                                value={this.state.jobOrderNo}
                                                readOnly
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>Transaction ID:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                type="text"
                                                value={this.state.transactionId}
                                                readOnly
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <h5 style={{ marginTop: '20px', marginBottom: '15px', borderTop: '1px solid #ddd', paddingTop: '15px' }}>
                                    Quantity and Transfer Location
                                </h5>

                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>Lot No:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                type="text"
                                                value={this.state.lotNo}
                                                readOnly
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>Manufacturing Date:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                type="text"
                                                value={this.state.manufacturingDate}
                                                readOnly
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>Qty:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                type="text"
                                                value={this.state.quantity}
                                                readOnly
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>From Location Code:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                type="text"
                                                value={this.state.fromLocationCode}
                                                readOnly
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>From Location Desc:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                type="text"
                                                value={this.state.fromLocationDescription}
                                                readOnly
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>To Location Code:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                type="text"
                                                value={this.state.toLocationCode}
                                                readOnly
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>To Location Desc:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                type="text"
                                                value={this.state.toLocationDescription}
                                                readOnly
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>Country:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                type="text"
                                                value={this.state.country}
                                                readOnly
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>Special Requirement:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                as="textarea"
                                                rows={3}
                                                value={this.state.specialRequirement}
                                                readOnly
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>

                                <h5 style={{ marginTop: '20px', marginBottom: '15px', borderTop: '1px solid #ddd', paddingTop: '15px' }}>
                                    Storage Location
                                </h5>

                                <FormGroup>
                                    <Row>
                                        <Col md={4}><strong>Storage Location:</strong></Col>
                                        <Col md={8}>
                                            <FormControl
                                                type="text"
                                                value={this.state.storageLocation || ''}
                                                readOnly
                                                style={{ backgroundColor: '#f9f9f9' }}
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>
                            </Form>
                        </Col>

                        <Col md={6} className="report-content page-break">
                            <Form>
                                <h5 style={{ marginTop: '0', marginBottom: '15px' }}>Status: </h5>
                                
                                <FormGroup>
                                    <div style={{ marginBottom: '10px' }}>
                                        <span style={{ 
                                            display: 'inline-block',
                                            width: '20px',
                                            height: '20px',
                                            backgroundColor: this.state.status === 'confirmed' ? '#28a745' : 'transparent',
                                            border: '1px solid #ddd',
                                            marginRight: '10px',
                                            verticalAlign: 'middle'
                                        }}></span>
                                        <strong>1. Accept</strong>
                                    </div>
                                    <div>
                                        <span style={{ 
                                            display: 'inline-block',
                                            width: '20px',
                                            height: '20px',
                                            backgroundColor: this.state.status === 'rejected' ? '#dc3545' : 'transparent',
                                            border: '1px solid #ddd',
                                            marginRight: '10px',
                                            verticalAlign: 'middle'
                                        }}></span>
                                        <strong>2. Reject</strong>
                                        {this.state.status === 'rejected' && this.state.rejectReason && (
                                            <div style={{ marginLeft: '30px', marginTop: '5px', color: '#dc3545', fontSize: '12px' }}>
                                                Reason: {this.state.rejectReason}
                                            </div>
                                        )}
                                    </div>
                                </FormGroup>

                                <h5 style={{ marginTop: '30px', marginBottom: '15px' }}>Photo:</h5>
                                <FormGroup>
                                    {this.state.photoUrl ? (
                                        <div style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center', backgroundColor: '#f9f9f9' }}>
                                            <img 
                                                src={this.state.photoUrl} 
                                                alt="Transfer Photo" 
                                                style={{ maxWidth: '100%', maxHeight: '300px' }}
                                            />
                                        </div>
                                    ) : (
                                        <div style={{ border: '1px solid #ddd', padding: '40px', textAlign: 'center', backgroundColor: '#f9f9f9', color: '#999' }}>
                                            No photo available
                                        </div>
                                    )}
                                </FormGroup>

                                <h5 style={{ marginTop: '30px', marginBottom: '15px' }}>Remarks:</h5>
                                <FormGroup>
                                    <FormControl
                                        as="textarea"
                                        rows={4}
                                        name="remarks"
                                        value={this.state.remarks}
                                        readOnly
                                        style={{ backgroundColor: '#f9f9f9' }}
                                    />
                                </FormGroup>

                                <h5 style={{ marginTop: '30px', marginBottom: '15px' }}>Issued by:</h5>
                                <FormGroup>
                                    <FormControl
                                        type="text"
                                        name="issuedBy"
                                        value={this.state.issuedBy}
                                        readOnly
                                        style={{ backgroundColor: '#f9f9f9' }}
                                    />
                                </FormGroup>

                                <h5 style={{ marginTop: '20px', marginBottom: '15px' }}>Received by:</h5>
                                <FormGroup>
                                    <FormControl
                                        type="text"
                                        name="acceptedBy"
                                        value={this.state.acceptedBy}
                                        readOnly
                                        style={{ backgroundColor: '#f9f9f9' }}
                                    />
                                </FormGroup>
                            </Form>
                        </Col>
                    </Row>
                </div>

                <div id="fg-transfer-report-table" style={{ display: 'none' }}>
                    <table border="1" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tr>
                            <th colSpan="2" style={{ backgroundColor: '#4990E2', color: '#fff', padding: '10px', textAlign: 'center' }}>FG Transfer Individual Report</th>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold', width: '40%' }}>Report No:</td>
                            <td style={{ padding: '8px' }}>{this.state.reportNo || ''}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Ref No:</td>
                            <td style={{ padding: '8px' }}>{this.state.refNo || ''}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Process Date:</td>
                            <td style={{ padding: '8px' }}>{this.state.processDate || ''}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Inspection:</td>
                            <td style={{ padding: '8px' }}>{this.state.inspection || ''}</td>
                        </tr>
                        <tr>
                            <th colSpan="2" style={{ backgroundColor: '#f0f0f0', padding: '8px', textAlign: 'left' }}>Part Information</th>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Part No:</td>
                            <td style={{ padding: '8px' }}>{this.state.partNo || ''}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Description:</td>
                            <td style={{ padding: '8px' }}>{this.state.description || ''}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Job Order No:</td>
                            <td style={{ padding: '8px' }}>{this.state.jobOrderNo || ''}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Transaction ID:</td>
                            <td style={{ padding: '8px' }}>{this.state.transactionId || ''}</td>
                        </tr>
                        <tr>
                            <th colSpan="2" style={{ backgroundColor: '#f0f0f0', padding: '8px', textAlign: 'left' }}>Quantity and Transfer Location</th>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Lot No:</td>
                            <td style={{ padding: '8px' }}>{this.state.lotNo || ''}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Manufacturing Date:</td>
                            <td style={{ padding: '8px' }}>{this.state.manufacturingDate || ''}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Qty:</td>
                            <td style={{ padding: '8px' }}>{this.state.quantity || ''}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>From Location Code:</td>
                            <td style={{ padding: '8px' }}>{this.state.fromLocationCode || ''}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>From Location Desc:</td>
                            <td style={{ padding: '8px' }}>{this.state.fromLocationDescription || ''}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>To Location Code:</td>
                            <td style={{ padding: '8px' }}>{this.state.toLocationCode || ''}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>To Location Desc:</td>
                            <td style={{ padding: '8px' }}>{this.state.toLocationDescription || ''}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Country:</td>
                            <td style={{ padding: '8px' }}>{this.state.country || ''}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Special Requirement:</td>
                            <td style={{ padding: '8px' }}>{this.state.specialRequirement || ''}</td>
                        </tr>
                        <tr>
                            <th colSpan="2" style={{ backgroundColor: '#f0f0f0', padding: '8px', textAlign: 'left' }}>Storage Location</th>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Storage Location:</td>
                            <td style={{ padding: '8px' }}>{this.state.storageLocation || ''}</td>
                        </tr>
                        <tr>
                            <th colSpan="2" style={{ backgroundColor: '#f0f0f0', padding: '8px', textAlign: 'left' }}>Status</th>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Status:</td>
                            <td style={{ padding: '8px' }}>{this.state.status || ''}</td>
                        </tr>
                        {this.state.status === 'rejected' && this.state.rejectReason && (
                            <tr>
                                <td style={{ padding: '8px', fontWeight: 'bold' }}>Reject Reason:</td>
                                <td style={{ padding: '8px', color: '#dc3545' }}>{this.state.rejectReason}</td>
                            </tr>
                        )}
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Remarks:</td>
                            <td style={{ padding: '8px' }}>{this.state.remarks || ''}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Issued by:</td>
                            <td style={{ padding: '8px' }}>{this.state.issuedBy || ''}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>Received by:</td>
                            <td style={{ padding: '8px' }}>{this.state.acceptedBy || ''}</td>
                        </tr>
                        {this.state.photoUrl && (
                            <tr>
                                <td style={{ padding: '8px', fontWeight: 'bold' }}>Photo:</td>
                                <td style={{ padding: '8px' }}>{this.state.photoUrl}</td>
                            </tr>
                        )}
                    </table>
                </div>

                <style>{`
                    @media print {
                        @page {
                            size: portrait;
                            margin: 0.5cm;
                        }
                        body {
                            margin: 0;
                            padding: 0;
                        }
                        .container {
                            width: 100% !important;
                            max-width: 100% !important;
                            margin: 0 !important;
                            padding: 10px !important;
                        }
                        .no-print {
                            display: none !important;
                        }
                        .page-break {
                            page-break-after: always;
                        }
                        .report-content {
                            page-break-inside: avoid;
                        }
                    }
                `}</style>
            </div>
        );
    }
}

function withParams(Component) {
    return (props) => {
        const params = useParams();
        return <Component {...props} params={params} />;
    };
}

const FGTransferSummaryReportWithParams = withParams(FGTransferSummaryReport);
export { FGTransferSummaryReportWithParams as FGTransferSummaryReport };
